import { cn } from '../../lib/cn';

// Base skeleton pulse
export function Skeleton({ className }: { className?: string }) {
  return (
    <div className={cn('animate-pulse rounded-lg bg-white/10', className)} />
  );
}

// KPI Card skeleton
export function KPICardSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn('bg-white/5 border border-white/10 rounded-2xl backdrop-blur-lg p-5', className)}>
      <Skeleton className="h-3 w-24 mb-3" />
      <Skeleton className="h-7 w-32 mb-2" />
      <Skeleton className="h-3 w-20" />
    </div>
  );
}

// KPI Grid skeleton - responsive 1->2->4 columns
export function KPIGridSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <KPICardSkeleton key={i} />
      ))}
    </div>
  );
}

// Table row skeleton
export function TableRowSkeleton({ columns = 5 }: { columns?: number }) {
  return (
    <tr className="animate-pulse border-b border-white/5">
      {Array.from({ length: columns }).map((_, i) => (
        <td key={i} className="px-4 py-3">
          <Skeleton className="h-4 w-full" />
        </td>
      ))}
    </tr>
  );
}

// Table skeleton with header + rows
export function TableSkeleton({ rows = 5, columns = 5 }: { rows?: number; columns?: number }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className="border-b border-white/10">
            {Array.from({ length: columns }).map((_, i) => (
              <th key={i} className="px-4 py-3">
                <Skeleton className="h-3 w-20" />
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: rows }).map((_, i) => (
            <TableRowSkeleton key={i} columns={columns} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

// Chart skeleton - pulsing placeholder
export function ChartSkeleton({ className, height = 'h-64' }: { className?: string; height?: string }) {
  return (
    <div className={cn('bg-white/5 border border-white/10 rounded-2xl p-6', className)}>
      <Skeleton className="h-4 w-40 mb-4" />
      <div className={cn('animate-pulse rounded-lg bg-white/[0.03] flex items-end justify-around gap-1 px-4 pb-4', height)}>
        {Array.from({ length: 12 }).map((_, i) => (
          <div
            key={i}
            className="bg-white/10 rounded-t w-full"
            style={{ height: `${20 + Math.random() * 60}%` }}
          />
        ))}
      </div>
    </div>
  );
}

// Page skeleton - full page with KPI grid + chart + table
export function PageSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-8 w-48 mb-2" />
      <KPIGridSkeleton />
      <ChartSkeleton />
      <TableSkeleton />
    </div>
  );
}

// Card skeleton
export function CardSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn('bg-white/5 border border-white/10 rounded-2xl p-6', className)}>
      <Skeleton className="h-5 w-32 mb-4" />
      <Skeleton className="h-4 w-full mb-2" />
      <Skeleton className="h-4 w-3/4 mb-2" />
      <Skeleton className="h-4 w-1/2" />
    </div>
  );
}
